import React, { useEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';

// Extend Window interface to include VITE_WORKER_URL and Stripe
declare global {
  interface Window {
    VITE_WORKER_URL?: string;
    Stripe?: any;
  }
}

const AlphaUpgrade: React.FC = () => {
  const [email, setEmail] = useState('');
  const [coupon, setCoupon] = useState('');
  const [cardElement, setCardElement] = useState<any>(null);
  const [stripe, setStripe] = useState<any>(null);
  const [cardErrors, setCardErrors] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const location = useLocation();
  const stripeKeyFetchedRef = useRef(false);
  const loggedInfoRef = useRef(false);

  useEffect(() => {
    // Get email from URL parameter if available
    const urlParams = new URLSearchParams(location.search);
    const emailParam = urlParams.get('email');
    
    // Pre-populate email field if parameter exists
    if (emailParam) {
      setEmail(emailParam);
    }
    
    // Get the API URL - use VITE_WORKER_URL if available, otherwise fallback to current host
    const apiUrl = window.VITE_WORKER_URL || import.meta.env.VITE_WORKER_URL || window.location.origin;
    
    // Only log once to avoid duplicate logs in StrictMode
    if (!loggedInfoRef.current) {
      loggedInfoRef.current = true;
      console.log('Email from URL parameter:', emailParam);
      console.log('Using API URL:', apiUrl);
    }
    
    // Development fallback key - this is a test key
    const FALLBACK_STRIPE_TEST_KEY = 'pk_test_51QgunAEFMAcX4JyIC3WxFFMXSAAB7RczTxGUx4Z4akr4Euo0jj3Weyh5yvSwSeX5GqgpJOfyerYY2AVBVVVcsu4X00GWg4XbeD';
    
    // Initialize Stripe with the fallback key
    const initializeStripeWithKey = (key: string) => {
      try {
        if (!window.Stripe) {
          console.error('Stripe.js not loaded. Make sure it is included in the HTML.');
          setCardErrors('Failed to initialize payment system. Please try again later.');
          return false;
        }
        
        const stripeInstance = window.Stripe(key);
        const elements = stripeInstance.elements();
        initializeStripeElements(stripeInstance, elements);
        return true;
      } catch (error) {
        console.error('Error initializing Stripe:', error);
        setCardErrors('Failed to initialize payment system. Please try again later.');
        return false;
      }
    };
    
    // Initialize with fallback key first
    initializeStripeWithKey(FALLBACK_STRIPE_TEST_KEY);
    
    // Then try to fetch the proper key in the background, but only once
    if (!stripeKeyFetchedRef.current) {
      stripeKeyFetchedRef.current = true;
      
      fetch(`${apiUrl}/api/stripe-public-key`)
        .then(response => {
          if (!response.ok) {
            throw new Error(`Failed to fetch Stripe public key: ${response.status}`);
          }
          return response.text();
        })
        .then(stripePublicKey => {
          // Check if the response looks like HTML (indicating an error)
          if (stripePublicKey.trim().startsWith('<!DOCTYPE') || 
              stripePublicKey.trim().startsWith('<html') || 
              stripePublicKey.trim().startsWith('<?xml')) {
            console.error('Received HTML instead of API key. Falling back to test key.');
            throw new Error('Invalid API key format received');
          }
          
          if (!loggedInfoRef.current) {
            console.log('Using server-provided Stripe key');
          }
          // Re-initialize Stripe with the fetched public key
          initializeStripeWithKey(stripePublicKey);
        })
        .catch(error => {
          console.error('Error fetching Stripe public key:', error);
          console.log('Continuing with fallback test key');
        });
    }
  }, [location.search]);

  // Function to initialize Stripe elements and set up event handlers
  const initializeStripeElements = (stripeInstance: any, elements: any) => {
    // Create card element
    const cardElement = elements.create('card', {
      style: {
        base: {
          color: '#f1f5f9',
          fontFamily: 'system-ui, -apple-system, sans-serif',
          fontSmoothing: 'antialiased',
          fontSize: '16px',
          '::placeholder': {
            color: '#94a3b8'
          }
        },
        invalid: {
          color: '#ef4444',
          iconColor: '#ef4444'
        }
      }
    });
    
    setStripe(stripeInstance);
    setCardElement(cardElement);
    
    // Mount the card element after the component has rendered
    setTimeout(() => {
      const cardElementContainer = document.getElementById('card-element');
      if (cardElementContainer) {
        cardElement.mount('#card-element');
        
        // Handle validation errors
        cardElement.addEventListener('change', function(event: any) {
          if (event.error) {
            setCardErrors(event.error.message);
          } else {
            setCardErrors('');
          }
        });
      }
    }, 100);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!stripe || !cardElement) {
      setCardErrors('Payment system not initialized. Please try again later.');
      return;
    }
    
    // Disable the submit button to prevent multiple clicks
    setIsProcessing(true);
    
    try {
      // Create payment method
      const { paymentMethod, error } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
        billing_details: {
          email: email
        }
      });
      
      if (error) {
        // Show error and re-enable button
        setCardErrors(error.message);
        setIsProcessing(false);
        return;
      }
      
      // Send to your server using the API URL
      const apiUrl = window.VITE_WORKER_URL || import.meta.env.VITE_WORKER_URL || window.location.origin;
      const response = await fetch(`${apiUrl}/api/create-subscription`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: email,
          paymentMethodId: paymentMethod.id,
          coupon: coupon.trim() || undefined
        })
      });
      
      // Check if response is ok before parsing JSON
      if (!response.ok) {
        throw new Error(`Server responded with status: ${response.status}`);
      }
      
      // Get response text first for debugging
      const responseText = await response.text();
      
      // Try to parse as JSON
      let result;
      try {
        result = JSON.parse(responseText);
      } catch (e) {
        console.error('Failed to parse JSON response:', responseText);
        throw new Error(`Invalid JSON response: ${responseText}`);
      }
      
      if (result.error) {
        // Show error
        setCardErrors(result.error);
        setIsProcessing(false);
      } else if (result.status === 'incomplete' || result.clientSecret) {
        // Handle 3D Secure authentication if needed
        const { error, paymentIntent } = await stripe.confirmCardPayment(
          result.clientSecret
        );
        
        if (error) {
          // Show error
          setCardErrors(error.message);
          setIsProcessing(false);
        } else {
          // Success
          setIsSuccess(true);
        }
      } else {
        // Success
        setIsSuccess(true);
      }
    } catch (error: any) {
      console.error('Error:', error);
      setCardErrors('An unexpected error occurred. Please try again.');
      setIsProcessing(false);
    }
  };

  const scrollToPaymentForm = () => {
    document.getElementById('payment-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="bg-slate-900 text-slate-100 min-h-screen">
      <header className="gradient-bg border-b border-slate-700 py-4">
        <div className="container mx-auto px-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src="/android-chrome-192x192.png" alt="askCHAD.io Logo" className="h-8 w-8" />
            <h1 className="text-xl font-bold">askCHAD.io</h1>
          </div>
          <a href="/" className="text-emerald-500 hover:text-emerald-400">Back to Chat</a>
        </div>
      </header>

      <main className="container mx-auto px-4 py-12 max-w-5xl">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold mb-4">Upgrade to ALPHA—Own Your Edge Now!</h1>
          <p className="text-xl text-slate-300 max-w-2xl mx-auto">
          Why settle for half-assed when you can dominate? Upgrading to Alpha gets you a <strong>premium, no-bullshit experience</strong>—zero ads, lightning-fast responses, and exclusive features that put you ahead of the pack.<br/>Stop wasting time and start winning. <strong>Make the call.</strong>
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 mb-12">
          <div className="bg-slate-800 rounded-lg p-8 border border-slate-700 shadow-lg">
            <h2 className="text-2xl font-bold mb-6">Free (Ad-Supported)</h2>
            <div className="text-3xl font-bold mb-6">$0<span className="text-slate-400 text-lg">/forever</span></div>
            <ul className="space-y-3 mb-8">
              <li className="flex items-start">
                <svg className="w-5 h-5 text-slate-400 mr-2 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                </svg>
                <span><strong>Ad-cluttered experience</strong>—distractions everywhere</span>
              </li>
              <li className="flex items-start">
                <svg className="w-5 h-5 text-slate-400 mr-2 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                </svg>
                <span><strong>Standard response length</strong>—bare minimum answers</span>
              </li>
              <li className="flex items-start">
                <svg className="w-5 h-5 text-slate-400 mr-2 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                </svg>
                <span><strong>Basic support</strong>—good luck getting help if you need it</span>
              </li>
            </ul>
            <button disabled className="w-full py-3 bg-slate-700 text-slate-300 rounded-md font-medium cursor-not-allowed">
              Current Plan
            </button>
          </div>

          <div className="bg-slate-800 rounded-lg p-8 border border-emerald-600 shadow-lg relative">
            <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-emerald-600 text-slate-900 px-4 py-1 rounded-full font-bold text-sm">
              RECOMMENDED
            </div>
            <h2 className="text-2xl font-bold mb-6">Alpha</h2>
            <div className="text-3xl font-bold mb-6">$20<span className="text-slate-400 text-lg">/year</span></div>
            <ul className="space-y-3 mb-8">
              <li className="flex items-start">
                <svg className="w-5 h-5 text-emerald-500 mr-2 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                </svg>
                <span><strong>No Ads</strong>—Clean, distraction-free interface to focus on what matters</span>
              </li>
              <li className="flex items-start">
                <svg className="w-5 h-5 text-emerald-500 mr-2 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                </svg>
                <span><strong>Instant responses</strong>—Get answers the second you need ‘em, no waiting </span>
              </li>
              <li className="flex items-start">
                <svg className="w-5 h-5 text-emerald-500 mr-2 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                </svg>
                <span><strong>Enhanced response length</strong>—Deeper, more detailed game plans to fix your shit</span>
              </li>
              <li className="flex items-start">
                <svg className="w-5 h-5 text-emerald-500 mr-2 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                </svg>
                <span><strong>VIP support</strong>—Priority assistance, you're at the top of the list </span>
              </li>
              <li className="flex items-start">
                <svg className="w-5 h-5 text-emerald-500 mr-2 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                </svg>
                <span><strong>Early access to new features</strong>—Be the first to get cutting-edge tools for total domination</span>
              </li>
            </ul>
            <button 
              id="checkout-button" 
              className="w-full py-3 bg-emerald-500 text-slate-900 rounded-md font-medium hover:bg-emerald-400 transition-colors"
              onClick={scrollToPaymentForm}
            >
              Upgrade Now
            </button>
          </div>
        </div>

        <div className="bg-slate-800 rounded-lg p-8 border border-slate-700 shadow-lg mb-12">
          <h2 className="text-2xl font-bold mb-6">Why Upgrade to Alpha? Stop Screwing Yourself Over!</h2>
          
          <div className="grid md:grid-cols-3 gap-6">
            <div className="feature-card bg-slate-700 p-6 rounded-lg">
              <div className="bg-emerald-900/50 w-12 h-12 rounded-full flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path>
                </svg>
              </div>
              <h3 className="text-lg font-bold mb-2">Lightning Fast</h3>
              <p className="text-slate-300">Tired of waiting around like a chump? Get instant responses to every damn question. Your life doesn't pause—neither should your solutions.</p>
            </div>
            
            <div className="feature-card bg-slate-700 p-6 rounded-lg">
              <div className="bg-emerald-900/50 w-12 h-12 rounded-full flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"></path>
                </svg>
              </div>
              <h3 className="text-lg font-bold mb-2">Ad-Free Domination</h3>
              <p className="text-slate-300">No more annoying ads clogging up your screen. Enjoy a clean, badass interface that lets you focus on becoming the man you're meant to be.</p>
            </div>
            
            <div className="feature-card bg-slate-700 p-6 rounded-lg">
              <div className="bg-emerald-900/50 w-12 h-12 rounded-full flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"></path>
                </svg>
              </div>
              <h3 className="text-lg font-bold mb-2">Hard-Hitting Responses</h3>
              <p className="text-slate-300">Get the full playbook, not just scraps. With maxed-out answers, CHAD will lay out every step you need to crush it—whether it's your marriage, body, or career.</p>
            </div>
          </div>
        </div>

        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold mb-4">$20/year for ALPHA—What's Your Excuse?</h1>
          <p className="text-xl text-slate-300 max-w-2xl mx-auto">This is just <strong>$20 per year</strong>—less than you spend on coffee in a week—for unlimited access to the tools real men use to <strong>win</strong>. No bullshit, no hidden fees, just premium performance when you need it most.</p><br/>
          <p className="text-xl text-slate-300 max-w-2xl mx-auto">Free version? That's for dabblers who stay stuck. Alpha is for warriors who take action. <strong>Upgrade Now</strong> to <strong>ALPHA</strong> and stop playing small. Every second you hesitate, <strong>you're falling behind</strong>. Let's do this.</p>
        </div>
        <div className="bg-slate-800 rounded-lg p-8 border border-slate-700 shadow-lg">
          <h2 className="text-2xl font-bold mb-6">Payment Information</h2>
          
          {isSuccess ? (
            <div className="bg-emerald-700 p-6 rounded-lg text-center">
              <svg className="w-16 h-16 text-emerald-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
              </svg>
              <h2 className="text-2xl font-bold text-white mb-4">Subscription Successful!</h2>
              <p className="text-emerald-100 mb-6">Your Alpha subscription has been activated. You now have access to all premium features!</p>
              <a href="/" className="inline-block px-6 py-3 bg-emerald-500 text-slate-900 rounded-md font-medium hover:bg-emerald-400 transition-colors">
                Return to Chat
              </a>
            </div>
          ) : (
            <form id="payment-form" className="space-y-6" onSubmit={handleSubmit}>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-slate-300 mb-1">Email</label>
                <input 
                  type="email" 
                  id="email" 
                  name="email" 
                  required 
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-md text-slate-100 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
              
              <div>
                <label htmlFor="coupon" className="block text-sm font-medium text-slate-300 mb-1">Coupon Code (Optional)</label>
                <input
                  type="text"
                  id="coupon"
                  name="coupon"
                  value={coupon}
                  onChange={(e) => setCoupon(e.target.value)}
                  placeholder="Enter coupon code"
                  className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-md text-slate-100 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
              
              <div>
                <label htmlFor="card-element" className="block text-sm font-medium text-slate-300 mb-1">Credit or debit card</label>
                <div id="card-element" className="p-4 bg-slate-700 border border-slate-600 rounded-md">
                  {/* Stripe Card Element will be inserted here */}
                </div>
                {cardErrors && <div id="card-errors" className="text-red-500 text-sm mt-2">{cardErrors}</div>}
              </div>
              
              <button
                type="submit"
                id="submit-button"
                disabled={isProcessing}
                className="w-full py-3 bg-emerald-500 text-slate-900 rounded-md font-medium hover:bg-emerald-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isProcessing ? 'Processing...' : 'Subscribe to Alpha ($20/year)'}
              </button>
              
              <p className="text-sm text-slate-400 text-center">
                Your Alpha subscription will begin immediately after payment. Cancel anytime.
              </p>
            </form>
          )}
        </div>
      </main>

      <footer className="bg-slate-800 border-t border-slate-700 py-8 mt-12">
        <div className="container mx-auto px-4 text-center">
          <p className="text-slate-400">© 2025 Lincoln Freedom Group, LLC. All rights reserved.</p>
          <div className="flex justify-center gap-4 mt-4">
            <a href="/terms.html" className="text-slate-400 hover:text-slate-300">Terms of Service</a>
            <a href="/privacy.html" className="text-slate-400 hover:text-slate-300">Privacy Policy</a>
          </div>
        </div>
      </footer>

      <style>
        {`
          .gradient-bg {
            background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
          }
          .feature-card {
            transition: transform 0.2s ease-in-out;
          }
          .feature-card:hover {
            transform: translateY(-5px);
          }
        `}
      </style>
    </div>
  );
};

export default AlphaUpgrade;
